import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

import {
    MAX_BATCH_SIZE,
    batchProblem,
    parsePeopleText,
    peopleProblems,
} from '../../../common/provisioningBatch.js';
import { describeProvisioningError, provisionUsers } from '../../../common/welcomeEmailApi.js';
import { SIGNUP_TARGETS } from '../../../common/signupLinkApi.js';
import { nameFromEmail } from '../../../common/welcomeEmailTemplate.js';
import {
    normalizeCompanyMembershipPermissionEntry,
    serializeCompanyMembershipPermissionEntry,
} from '../setupShared.js';
import UserAccessFields, { PERMISSION_FIELDS } from './UserAccessFields.jsx';
import WelcomeEmailChoice from './WelcomeEmailChoice.jsx';
import WelcomeEmailPreview from './WelcomeEmailPreview.jsx';

const EMPTY_STATE = {
    step: 'form',
    pasteText: '',
    people: [],
    wasteReceiverIds: [],
    companyIds: [],
    permissionsByCompanyId: {},
    templateId: '',
    target: 'insights',
    results: null,
};

const USER_OUTCOME_LABELS = {
    created: 'Oprettet',
    exists: 'Findes allerede – ikke ændret',
    failed: 'Kunne ikke oprettes',
};
const EMAIL_OUTCOME_LABELS = { sent: 'Sendt', failed: 'Ikke sendt' };

// Creates a Provisioning Batch: up to 50 people who share one set of access, each sent the
// chosen Welcome Email. Never creates admins.
const CreateUsersDialog = ({
    open,
    onClose,
    templates,
    existingEmails,
    wasteReceivers,
    companyOptionIds,
    getWasteReceiverDisplayLabel,
    getCompanyDisplayLabel,
    loadingReceivers,
    loadingCompanies,
    showMessage,
}) => {
    const [state, setState] = useState(EMPTY_STATE);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState('');
    const update = (patch) => setState((current) => ({ ...current, ...patch }));

    useEffect(() => {
        if (open) {
            setState(EMPTY_STATE);
            setError('');
        }
    }, [open]);

    useEffect(() => {
        if (!open || templates.length === 0) return;
        setState((current) =>
            templates.some((template) => template.id === current.templateId)
                ? current
                : { ...current, templateId: templates[0].id },
        );
    }, [open, templates]);

    const { step, people, companyIds, wasteReceiverIds, permissionsByCompanyId, templateId, target } = state;
    const template = templates.find((candidate) => candidate.id === templateId) || null;
    const rowProblems = useMemo(() => peopleProblems(people), [people]);
    const listProblem = batchProblem(people);
    const canContinue = !listProblem && Boolean(template);

    const addPastedPeople = () => {
        const parsed = parsePeopleText(state.pasteText);
        if (parsed.length === 0) return;
        update({ people: [...people, ...parsed], pasteText: '' });
    };

    const updatePerson = (index, key, value) => {
        update({
            people: people.map((person, current) => (current === index ? { ...person, [key]: value } : person)),
        });
    };

    const removePerson = (index) => {
        update({ people: people.filter((_, current) => current !== index) });
    };

    const getPermission = (companyId) =>
        normalizeCompanyMembershipPermissionEntry(permissionsByCompanyId[companyId]);

    const changeCompanies = (nextCompanyIds) => {
        update({
            companyIds: nextCompanyIds,
            permissionsByCompanyId: nextCompanyIds.reduce((next, companyId) => {
                next[companyId] = getPermission(companyId);
                return next;
            }, {}),
        });
    };

    const changePermission = (companyId, key, checked) => {
        update({
            permissionsByCompanyId: {
                ...permissionsByCompanyId,
                [companyId]: { ...getPermission(companyId), [key]: checked },
            },
        });
    };

    const submit = async () => {
        setRunning(true);
        setError('');
        try {
            const result = await provisionUsers({
                people: people.map((person) => ({ email: person.email.trim(), name: person.name.trim() })),
                access: {
                    waste_receivers: wasteReceiverIds,
                    companies: companyIds,
                    company_memberships: companyIds.reduce((next, companyId) => {
                        next[companyId] = serializeCompanyMembershipPermissionEntry(getPermission(companyId));
                        return next;
                    }, {}),
                },
                templateId,
                target,
            });
            update({ step: 'results', results: result.results || [] });
            const created = (result.results || []).filter((entry) => entry.outcome === 'created').length;
            showMessage(`${created} ${created === 1 ? 'bruger' : 'brugere'} oprettet.`);
        } catch (provisionError) {
            console.error('Error provisioning users:', provisionError);
            setError(describeProvisioningError(provisionError));
        } finally {
            setRunning(false);
        }
    };

    const permissionSummary = (companyId) => {
        const permission = getPermission(companyId);
        const granted = PERMISSION_FIELDS.filter(({ key }) => permission[key]).map(({ label }) => label);
        return granted.length > 0 ? granted.join(', ') : 'Ingen ekstra rettigheder';
    };

    const renderForm = () => (
        <>
            <div className="setup-form-section">
                <div className="setup-form-section-header">
                    <h3>Personer ({people.length} / {MAX_BATCH_SIZE})</h3>
                </div>
                <TextField
                    label="Indsæt emails"
                    helperText='Én person pr. linje: email, eventuelt efterfulgt af navn (adskilt af tab, komma eller semikolon) – eller "Navn <email>".'
                    value={state.pasteText}
                    onChange={(event) => update({ pasteText: event.target.value })}
                    multiline
                    minRows={3}
                    inputProps={{ spellCheck: false }}
                />
                <div className="welcome-inline-actions">
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={addPastedPeople}
                        disabled={!state.pasteText.trim()}
                    >
                        Tilføj til listen
                    </Button>
                </div>

                {people.length > 0 && (
                    <div className="welcome-people-table-wrapper">
                        <table className="welcome-people-table">
                            <thead>
                                <tr>
                                    <th scope="col">Email</th>
                                    <th scope="col">Navn</th>
                                    <th scope="col" aria-label="Fjern" />
                                </tr>
                            </thead>
                            <tbody>
                                {people.map((person, index) => {
                                    const problem = rowProblems[index];
                                    const exists =
                                        !problem && existingEmails.has(person.email.trim().toLowerCase());

                                    return (
                                        // Rows have no identity beyond their position while being edited.
                                        <tr key={index}>
                                            <td>
                                                <TextField
                                                    value={person.email}
                                                    onChange={(event) => updatePerson(index, 'email', event.target.value)}
                                                    size="small"
                                                    fullWidth
                                                    error={Boolean(problem)}
                                                    helperText={
                                                        problem
                                                        || (exists ? 'Har allerede en bruger – springes over.' : undefined)
                                                    }
                                                    inputProps={{ 'aria-label': `Email ${index + 1}` }}
                                                />
                                            </td>
                                            <td>
                                                <TextField
                                                    value={person.name}
                                                    onChange={(event) => updatePerson(index, 'name', event.target.value)}
                                                    placeholder={nameFromEmail(person.email)}
                                                    size="small"
                                                    fullWidth
                                                    inputProps={{ 'aria-label': `Navn ${index + 1}` }}
                                                />
                                            </td>
                                            <td>
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Fjern ${person.email || 'række'}`}
                                                    onClick={() => removePerson(index)}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {people.length > MAX_BATCH_SIZE && (
                    <Alert severity="warning">Højst {MAX_BATCH_SIZE} personer ad gangen.</Alert>
                )}
            </div>

            <div className="setup-form-section">
                <div className="setup-form-section-header">
                    <h3>Adgang</h3>
                </div>
                <p className="welcome-section-note">
                    Alle på listen får den samme adgang. Uden virksomheder og affaldsmodtagere bliver de oprettet
                    uden adgang til noget.
                </p>
                <UserAccessFields
                    wasteReceiverIds={wasteReceiverIds}
                    companyIds={companyIds}
                    wasteReceiverOptions={wasteReceivers.map((receiver) => receiver.id)}
                    companyOptionIds={companyOptionIds}
                    getPermission={getPermission}
                    onWasteReceiversChange={(next) => update({ wasteReceiverIds: next })}
                    onCompaniesChange={changeCompanies}
                    onPermissionChange={changePermission}
                    getWasteReceiverDisplayLabel={getWasteReceiverDisplayLabel}
                    getCompanyDisplayLabel={getCompanyDisplayLabel}
                    loadingReceivers={loadingReceivers}
                    loadingCompanies={loadingCompanies}
                    wasteReceiverHelperText="Hvilke affaldsmodtagere brugerne har adgang til."
                />
            </div>

            <WelcomeEmailChoice
                templates={templates}
                templateId={template ? templateId : ''}
                onTemplateChange={(next) => update({ templateId: next })}
                target={target}
                onTargetChange={(next) => update({ target: next })}
            />
        </>
    );

    const renderConfirm = () => {
        const first = people[0];

        return (
            <>
                <Alert severity="info">
                    {people.length} {people.length === 1 ? 'bruger oprettes' : 'brugere oprettes'}, og hver får en
                    velkomstmail med et link til at vælge adgangskode. Linket virker i 14 dage. Eksisterende
                    brugere springes over.
                </Alert>
                <div className="setup-field-grid">
                    <div className="setup-field-label">Affaldsmodtagere</div>
                    <div>
                        {wasteReceiverIds.length > 0
                            ? wasteReceiverIds.map(getWasteReceiverDisplayLabel).join(', ')
                            : 'Ingen'}
                    </div>
                    <div className="setup-field-label">Virksomheder</div>
                    <div>
                        {companyIds.length > 0 ? (
                            <ul className="welcome-summary-list">
                                {companyIds.map((companyId) => (
                                    <li key={companyId}>
                                        {getCompanyDisplayLabel(companyId)}: {permissionSummary(companyId)}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            'Ingen'
                        )}
                    </div>
                    <div className="setup-field-label">Skabelon</div>
                    <div>{template?.name}</div>
                    <div className="setup-field-label">Videre til</div>
                    <div>{SIGNUP_TARGETS[target]?.label}</div>
                </div>
                <div className="setup-form-section">
                    <div className="setup-form-section-header">
                        <h3>Forhåndsvisning for {first.email.trim()}</h3>
                    </div>
                    {template && (
                        <WelcomeEmailPreview
                            template={template}
                            email={first.email.trim()}
                            name={first.name.trim() || nameFromEmail(first.email)}
                        />
                    )}
                </div>
            </>
        );
    };

    const renderResults = () => {
        const results = state.results || [];
        const sendFailures = results.filter((entry) => entry.email_outcome === 'failed').length;

        return (
            <>
                {sendFailures > 0 && (
                    <Alert severity="warning">
                        {sendFailures === 1 ? 'Én velkomstmail' : `${sendFailures} velkomstmails`} kunne ikke sendes.
                        Brugerne er oprettet – send mailen igen fra brugeroversigten.
                    </Alert>
                )}
                <div className="welcome-people-table-wrapper">
                    <table className="welcome-people-table">
                        <thead>
                            <tr>
                                <th scope="col">Email</th>
                                <th scope="col">Navn</th>
                                <th scope="col">Bruger</th>
                                <th scope="col">Velkomstmail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map((entry) => (
                                <tr key={entry.email}>
                                    <td>{entry.email}</td>
                                    <td>{entry.name}</td>
                                    <td className={`welcome-outcome welcome-outcome-${entry.outcome}`}>
                                        {USER_OUTCOME_LABELS[entry.outcome] || entry.outcome}
                                    </td>
                                    <td className={`welcome-outcome welcome-outcome-${entry.email_outcome || 'none'}`}>
                                        {EMAIL_OUTCOME_LABELS[entry.email_outcome] || '–'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={running ? undefined : onClose}
            className="setup-dialog"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                {step === 'results' ? 'Resultat' : step === 'confirm' ? 'Bekræft og send' : 'Opret brugere'}
            </DialogTitle>
            <DialogContent className="setup-user-dialog-content">
                {step === 'form' && renderForm()}
                {step === 'confirm' && renderConfirm()}
                {step === 'results' && renderResults()}
                {running && <LinearProgress />}
                {error && <Alert severity="error">{error}</Alert>}
            </DialogContent>
            <DialogActions>
                {step === 'form' && (
                    <>
                        {listProblem && people.length > 0 && (
                            <span className="welcome-actions-note">{listProblem}</span>
                        )}
                        <Button onClick={onClose}>Annuller</Button>
                        <Button variant="contained" onClick={() => update({ step: 'confirm' })} disabled={!canContinue}>
                            Videre
                        </Button>
                    </>
                )}
                {step === 'confirm' && (
                    <>
                        <Button onClick={() => update({ step: 'form' })} disabled={running}>
                            Tilbage
                        </Button>
                        <Button variant="contained" startIcon={<SendIcon />} onClick={submit} disabled={running}>
                            Opret og send
                        </Button>
                    </>
                )}
                {step === 'results' && (
                    <Button variant="contained" onClick={onClose}>
                        Luk
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CreateUsersDialog;
